import { useState, useEffect, useMemo } from 'react';
import unifiedApi from '../services/unifiedApi';
import eventBus, { ASSIGNMENT_EVENTS, AssignmentEventHelpers } from '../utils/eventBus';
import { toast } from 'sonner';
import logger from '@/utils/logger';

// ─── Pure helpers ────────────────────────────────────────────────────────────

export function generateCircuitCode() {
  return `CIR_${Date.now().toString().slice(-8)}`;
}

export function buildInboundFallback(outboundStops) {
  const schoolStop = outboundStops.find((s) => s.isSchool);
  const nonSchool = outboundStops.filter((s) => !s.isSchool);
  const reversed = [...nonSchool].reverse();
  return schoolStop ? [schoolStop, ...reversed] : reversed;
}

export function buildInboundForEdit(newOutboundStops, previousInboundStops) {
  const schoolStop = newOutboundStops.find((s) => s.isSchool);
  const outboundNonSchool = newOutboundStops.filter((s) => !s.isSchool);
  const previousInboundNonSchool = (previousInboundStops || []).filter((s) => !s.isSchool);

  const outboundIdSet = new Set(outboundNonSchool.map((s) => s.id));
  const previousIdSet = new Set(previousInboundNonSchool.map((s) => s.id));

  const kept = previousInboundNonSchool.filter((s) => outboundIdSet.has(s.id));
  const added = outboundNonSchool.filter((s) => !previousIdSet.has(s.id));

  return schoolStop ? [schoolStop, ...kept, ...added] : [...kept, ...added];
}

export function validateOrdersCoherence(morningStops, eveningStops, { busParksAtHome = false } = {}) {
  const errors = [];
  const warnings = [];

  const morning = morningStops.filter((s) => !s.isSchool);
  const evening = eveningStops.filter((s) => !s.isSchool);

  const morningIds = morning.map((s) => s.id);
  const eveningIds = evening.map((s) => s.id);

  const morningSet = new Set(morningIds);
  const eveningSet = new Set(eveningIds);

  if (morningIds.length !== morningSet.size) {
    errors.push("Des arrêts sont en double dans l'ordre du matin.");
  }
  if (eveningIds.length !== eveningSet.size) {
    errors.push("Des arrêts sont en double dans l'ordre du soir.");
  }

  const missingFromEvening = morningIds.filter((id) => !eveningSet.has(id));
  const missingFromMorning = eveningIds.filter((id) => !morningSet.has(id));

  if (missingFromEvening.length > 0) {
    errors.push(`${missingFromEvening.length} arrêt(s) du matin absent(s) du soir.`);
  }
  if (missingFromMorning.length > 0) {
    errors.push(`${missingFromMorning.length} arrêt(s) du soir absent(s) du matin.`);
  }

  if (!busParksAtHome && errors.length === 0) {
    const identical = morningIds.length === eveningIds.length && morningIds.every((id, i) => id === eveningIds[i]);
    if (identical) {
      warnings.push("L'ordre du soir est identique à celui du matin — vérifiez si c'est intentionnel.");
    }
  }

  return { valid: errors.length === 0, errors, warnings };
}

export function reorderNonSchoolStops(list, sourceIndex, targetIndex) {
  const nonSchool = list.filter((s) => !s.isSchool);
  if (sourceIndex < 0 || targetIndex < 0 || sourceIndex >= nonSchool.length || targetIndex >= nonSchool.length)
    return list;

  const reordered = [...nonSchool];
  const [moved] = reordered.splice(sourceIndex, 1);
  reordered.splice(targetIndex, 0, moved);

  let cursor = 0;
  return list.map((s) => {
    if (s.isSchool) return s;
    return reordered[cursor++];
  });
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useAssignmentManagement = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('overview');

  const [stops, setStops] = useState([]);
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [schools, setSchools] = useState([]);

  const [selectedStops, setSelectedStops] = useState([]);
  const [optimizedOrder, setOptimizedOrder] = useState([]);
  const [selectedBus, setSelectedBus] = useState('');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [busParksAtHome, setBusParksAtHome] = useState(false);
  const [circuitName, setCircuitName] = useState('');
  const [circuitDescription, setCircuitDescription] = useState('');
  const [circuitCode, setCircuitCode] = useState('');
  const [circuitStatus, setCircuitStatus] = useState('active');
  const [editingRouteId, setEditingRouteId] = useState(null);

  const [isCreating, setIsCreating] = useState(false);
  const [inboundOrder, setInboundOrder] = useState([]);
  const [orderingModalOpen, setOrderingModalOpen] = useState(false);
  const [orderingDirection, setOrderingDirection] = useState('outbound');
  const [orderingStep, setOrderingStep] = useState(1);
  const [draftOutboundOrder, setDraftOutboundOrder] = useState([]);
  const [draftInboundOrder, setDraftInboundOrder] = useState([]);
  const [dragInsertIndex, setDragInsertIndex] = useState(null);
  const [coherenceResult, setCoherenceResult] = useState(null);
  const [hasOutboundOrderBeenValidated, setHasOutboundOrderBeenValidated] = useState(false);
  const [hasVisitedStep2InSession, setHasVisitedStep2InSession] = useState(false);

  const [viewModalRoute, setViewModalRoute] = useState(null);
  const [viewModalStopsRaw, setViewModalStopsRaw] = useState([]);
  const [viewModalLoading, setViewModalLoading] = useState(false);
  const [viewModalError, setViewModalError] = useState(null);
  const [viewModalSearch, setViewModalSearch] = useState('');
  const [viewDirection, setViewDirection] = useState('outbound');

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [routeToDelete, setRouteToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // ─── Derived ──────────────────────────────────────────────────────────────

  const viewModalStopsSorted = useMemo(() => {
    if (!viewModalStopsRaw.length) return [];
    const col = viewDirection === 'outbound' ? 'outbound_order' : 'inbound_order';
    return [...viewModalStopsRaw].sort((a, b) => (Number(a[col]) || 0) - (Number(b[col]) || 0));
  }, [viewModalStopsRaw, viewDirection]);

  const dynamicStats = {
    totalStops: stops.length,
    availableBuses: buses.filter((b) => !b.hasAssignedRoute).length,
    availableDrivers: drivers.filter((d) => !d.hasAssignedRoute).length,
    activeRoutes: routes.filter((r) => r.status === 'active').length,
    selectedStops: selectedStops.length,
    estimatedDuration: optimizedOrder.length * 5,
  };

  // ─── Data loading ─────────────────────────────────────────────────────────

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [stopsRes, busesRes, driversRes, routesRes, schoolRes] = await Promise.all([
        unifiedApi.stops.getAll(),
        unifiedApi.buses.getAll(),
        unifiedApi.drivers.getAll(),
        unifiedApi.routes.getAll(),
        unifiedApi.schools.getCurrent().catch(() => ({ data: null })),
      ]);

      const rawStops = stopsRes.data || [];
      const rawRoutes = routesRes.data || [];

      const routeStopsData = await Promise.all(
        rawRoutes.map(async (route) => {
          try {
            const res = await unifiedApi.routes.getStops(route.id, { direction: 'outbound' });
            return {
              routeId: route.id,
              routeName: route.route_name || route.name,
              routeCode: route.route_code || route.code,
              stops: res.data || [],
            };
          } catch {
            return {
              routeId: route.id,
              routeName: route.route_name || route.name,
              routeCode: route.route_code || route.code,
              stops: [],
            };
          }
        }),
      );

      const enrichedRoutes = rawRoutes.map((route) => {
        const rs = routeStopsData.find((r) => r.routeId === route.id);
        return { ...route, stopsCount: rs?.stops?.length ?? 0 };
      });

      const enrichedStops = rawStops.map((stop) => {
        const assigned = routeStopsData.filter((rd) => rd.stops.some((rs) => rs.stop_id === stop.id));
        const activeAssigned = assigned.filter((rd) => {
          const r = enrichedRoutes.find((r) => r.id === rd.routeId);
          return r && r.status === 'active';
        });
        const inactiveAssigned = assigned.filter((rd) => {
          const r = enrichedRoutes.find((r) => r.id === rd.routeId);
          return r && r.status !== 'active';
        });
        return {
          ...stop,
          isAssigned: assigned.length > 0,
          assignedToRoutes: assigned.map((rd) => ({ id: rd.routeId, name: rd.routeName, code: rd.routeCode })),
          assignedToInactiveCircuits: inactiveAssigned.length > 0,
          assignmentStatus: activeAssigned.length > 0 ? 'assigned' : 'available',
        };
      });

      setStops(enrichedStops);

      const busIdsWithRoutes = new Set(
        enrichedRoutes.filter((r) => r.bus_id && r.status === 'active').map((r) => r.bus_id),
      );
      setBuses(
        (busesRes.data || [])
          .filter((b) => b.status === 'active')
          .map((b) => ({ ...b, hasAssignedRoute: busIdsWithRoutes.has(b.id) })),
      );

      const driverIdsWithRoutes = new Set(
        enrichedRoutes.filter((r) => r.driver_id && r.status === 'active').map((r) => r.driver_id),
      );
      setDrivers(
        (driversRes.data || [])
          .filter((d) => d.status === 'active')
          .map((d) => ({ ...d, hasAssignedRoute: driverIdsWithRoutes.has(d.id) })),
      );

      setRoutes(enrichedRoutes);
      if (schoolRes?.data) setSchools([schoolRes.data]);
    } catch (err) {
      logger.error('Erreur chargement données assignation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    const cleanups = [
      eventBus.on(ASSIGNMENT_EVENTS.ASSIGNMENT_CHANGED, () => loadData()),
      eventBus.on(ASSIGNMENT_EVENTS.BUS_UPDATED, () => loadData()),
      eventBus.on(ASSIGNMENT_EVENTS.DRIVER_UPDATED, () => loadData()),
      eventBus.on(ASSIGNMENT_EVENTS.ROUTE_UPDATED, () => loadData()),
    ];
    return () => cleanups.forEach((c) => c());
  }, []);

  useEffect(() => {
    if (mode === 'create' && !editingRouteId && !circuitCode) {
      setCircuitCode(generateCircuitCode());
    }
  }, [mode, editingRouteId]);

  useEffect(() => {
    if (mode === 'create' && !editingRouteId && schools.length > 0 && selectedStops.length === 0) {
      const school = schools[0];
      const schoolAsStop = {
        id: `school-${school.id}`,
        name: school.name,
        stop_name: school.name,
        address: school.address || '',
        latitude: school.latitude,
        longitude: school.longitude,
        student_count: 0,
        isSchool: true,
        assignmentStatus: 'available',
        isAssigned: false,
        assignedToRoutes: [],
      };
      setSelectedStops([schoolAsStop]);
      setOptimizedOrder([schoolAsStop]);
      setInboundOrder([schoolAsStop]);
    }
  }, [mode, schools, editingRouteId]);

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const resetForm = () => {
    setSelectedStops([]);
    setOptimizedOrder([]);
    setInboundOrder([]);
    setSelectedBus('');
    setSelectedDriver('');
    setBusParksAtHome(false);
    setCircuitName('');
    setCircuitDescription('');
    setCircuitCode(generateCircuitCode());
    setCircuitStatus('active');
    setEditingRouteId(null);
    setHasOutboundOrderBeenValidated(false);
  };

  const handleStopSelection = (stop, isSelected) => {
    if (isSelected && stop.isSchool) return;

    const newSelected = isSelected ? selectedStops.filter((s) => s.id !== stop.id) : [...selectedStops, stop];

    setSelectedStops(newSelected);
    if (editingRouteId) {
      // Preserve both orders when adding/removing stops — just splice the stop in/out
      setInboundOrder(buildInboundForEdit(newSelected, inboundOrder));
      setOptimizedOrder(buildInboundForEdit(newSelected, optimizedOrder));
    } else {
      // Creation: selection order = morning (matin), evening = auto-inverse
      setInboundOrder(newSelected);
      setOptimizedOrder(buildInboundFallback(newSelected));
    }
  };

  const handleCreateCircuit = async () => {
    const stopsWithoutSchool = optimizedOrder.filter((s) => !s.isSchool);
    if (!circuitName.trim() || stopsWithoutSchool.length === 0) {
      alert('Veuillez saisir un nom de circuit et sélectionner au moins un arrêt.');
      return;
    }

    try {
      setIsCreating(true);
      const outbound = optimizedOrder.filter((s) => !s.isSchool);
      const inbound = inboundOrder.filter((s) => !s.isSchool);
      const inboundMap = new Map(inbound.map((s, i) => [s.id, i + 1]));

      const circuitData = {
        route_name: circuitName.trim(),
        route_code: circuitCode.trim() || generateCircuitCode(),
        description: circuitDescription.trim() || `Circuit avec ${outbound.length} arrêts`,
        type: 'regular',
        status: circuitStatus,
        stops: outbound.map((s, i) => ({
          stop_id: s.id,
          outbound_order: i + 1,
          inbound_order: inboundMap.get(s.id) || outbound.length - i,
          pickup_time: null,
          dropoff_time: null,
        })),
        bus_id: selectedBus || null,
        driver_id: selectedDriver || null,
        bus_parks_at_home: busParksAtHome,
        start_location: outbound[0]?.name || outbound[0]?.stop_name || '',
        end_location: outbound[outbound.length - 1]?.name || outbound[outbound.length - 1]?.stop_name || '',
        estimated_duration: outbound.length * 5,
        distance: 0,
      };

      const response = editingRouteId
        ? await unifiedApi.routes.update(editingRouteId, circuitData)
        : await unifiedApi.routes.create(circuitData);

      if (response.success) {
        AssignmentEventHelpers.emitRouteAssignment(
          editingRouteId || response.data.id,
          selectedBus || null,
          selectedDriver || null,
          editingRouteId ? 'updated' : 'created',
        );
        await loadData();
        resetForm();
        setMode('overview');
        toast.success(editingRouteId ? 'Circuit modifié avec succès !' : 'Circuit créé avec succès !');
      } else {
        throw new Error(response.message || 'Erreur lors de la création du circuit');
      }
    } catch (err) {
      let msg = err.message;
      if (err.responseData?.data?.error) msg = err.responseData.data.error;
      else if (err.responseData?.data?.message) msg = err.responseData.data.message;
      toast.error(msg);
    } finally {
      setIsCreating(false);
    }
  };

  const openOrderingModal = () => {
    setDraftOutboundOrder([...optimizedOrder]);
    setDraftInboundOrder(inboundOrder.length ? [...inboundOrder] : buildInboundFallback(optimizedOrder));
    setOrderingDirection('inbound');
    setOrderingStep(1);
    setHasVisitedStep2InSession(false);
    setOrderingModalOpen(true);
  };

  const closeOrderingModal = () => {
    setOrderingModalOpen(false);
    setOrderingStep(1);
    setDragInsertIndex(null);
    setCoherenceResult(null);
  };

  const goToOrderingStep2 = () => {
    if (!hasOutboundOrderBeenValidated && !hasVisitedStep2InSession) {
      setDraftOutboundOrder(buildInboundFallback(draftInboundOrder));
    }
    setHasVisitedStep2InSession(true);
    setOrderingDirection('outbound');
    setOrderingStep(2);
  };

  const goBackToStep1 = () => {
    setOrderingStep(1);
    setOrderingDirection('inbound');
  };

  const resetEveningToInverse = () => {
    setDraftOutboundOrder(buildInboundFallback(draftInboundOrder));
  };

  const handleDraftReorder = (sourceIndex, targetIndex) => {
    if (sourceIndex === targetIndex || sourceIndex < 0 || targetIndex < 0) return;
    if (orderingDirection === 'outbound') {
      setDraftOutboundOrder((prev) => reorderNonSchoolStops(prev, sourceIndex, targetIndex));
    } else {
      setDraftInboundOrder((prev) => reorderNonSchoolStops(prev, sourceIndex, targetIndex));
    }
  };

  const validateOrderingModal = () => {
    const result = validateOrdersCoherence(draftInboundOrder, draftOutboundOrder, { busParksAtHome });
    setCoherenceResult(result.errors.length === 0 && result.warnings.length === 0 ? null : result);
    if (!result.valid) return;
    if (result.warnings.length > 0) toast.warning(result.warnings[0]);
    setOptimizedOrder(draftOutboundOrder);
    setInboundOrder(draftInboundOrder.length ? draftInboundOrder : buildInboundFallback(draftOutboundOrder));
    setSelectedStops(draftInboundOrder.length ? draftInboundOrder : draftOutboundOrder);
    setHasOutboundOrderBeenValidated(true);
    setOrderingModalOpen(false);
  };

  const handleViewRoute = async (route) => {
    if (!route?.id) return;
    setViewModalRoute(route);
    setViewDirection('outbound');
    setViewModalStopsRaw([]);
    setViewModalError(null);
    setViewModalLoading(true);
    try {
      const res = await unifiedApi.routes.getStops(route.id, { direction: 'outbound' });
      const list = res?.data || [];
      setViewModalStopsRaw(Array.isArray(list) ? list : []);
    } catch (err) {
      logger.error('Erreur chargement arrêts du circuit:', err);
      setViewModalError(err?.message || 'Impossible de charger les arrêts');
      setViewModalStopsRaw([]);
    } finally {
      setViewModalLoading(false);
    }
  };

  const closeViewModal = () => {
    setViewModalRoute(null);
    setViewModalStopsRaw([]);
    setViewModalError(null);
    setViewModalSearch('');
  };

  const handleEditRoute = async (route) => {
    if (!route?.id) return;
    try {
      const [outRes, inRes] = await Promise.all([
        unifiedApi.routes.getStops(route.id, { direction: 'outbound' }),
        unifiedApi.routes.getStops(route.id, { direction: 'inbound' }),
      ]);
      const outboundRouteStops = outRes?.data || [];
      const inboundRouteStops = inRes?.data || [];

      const toUiStop = (routeStop) => {
        const stopId = routeStop.stop_id || routeStop.stop?.id || routeStop.id;
        const fullStop = stops.find((s) => s.id === stopId);
        if (!fullStop) {
          const sd = routeStop.stop || routeStop;
          if (!sd.latitude || !sd.longitude) return null;
          return {
            id: stopId,
            name: sd.name || sd.stop_name || 'Arrêt',
            stop_name: sd.stop_name || sd.name || 'Arrêt',
            address: sd.address || '',
            latitude: sd.latitude,
            longitude: sd.longitude,
            student_count: sd.student_count || 0,
            isSchool: sd.isSchool || false,
            assignmentStatus: 'available',
            isAssigned: false,
            assignedToRoutes: [],
            outbound_order: routeStop.outbound_order ?? 0,
            inbound_order: routeStop.inbound_order ?? 0,
          };
        }
        return {
          ...fullStop,
          outbound_order: routeStop.outbound_order ?? 0,
          inbound_order: routeStop.inbound_order ?? 0,
        };
      };

      const outboundStops = outboundRouteStops.map(toUiStop).filter(Boolean);
      const outboundById = new Map(outboundStops.map((s) => [s.id, s]));
      const inboundStops = inboundRouteStops
        .map((rs) => {
          const id = rs.stop_id || rs.stop?.id || rs.id;
          return outboundById.get(id) || toUiStop(rs);
        })
        .filter(Boolean);

      setEditingRouteId(route.id);
      setCircuitName(route.route_name || route.name || '');
      setCircuitCode(route.route_code || route.code || generateCircuitCode());
      setCircuitStatus(route.status || 'active');
      setCircuitDescription(route.description || '');
      setSelectedBus(route.bus_id || '');
      setSelectedDriver(route.driver_id || '');
      setBusParksAtHome(route.bus_parks_at_home || false);

      let finalStops = outboundStops;
      let finalInbound = inboundStops;

      const hasSchool = outboundStops.some((s) => s.isSchool) || inboundStops.some((s) => s.isSchool);
      if (!hasSchool && schools.length > 0) {
        const school = schools[0];
        const schoolAsStop = {
          id: `school-${school.id}`,
          name: school.name,
          stop_name: school.name,
          address: school.address || '',
          latitude: school.latitude,
          longitude: school.longitude,
          student_count: 0,
          isSchool: true,
          assignmentStatus: 'available',
          isAssigned: false,
          assignedToRoutes: [],
          outbound_order: 0,
          inbound_order: 0,
        };
        finalStops = [schoolAsStop, ...outboundStops];
        finalInbound = [schoolAsStop, ...inboundStops];
      }

      setSelectedStops(finalStops);
      setOptimizedOrder(finalStops);
      setInboundOrder(finalInbound.length ? finalInbound : buildInboundFallback(finalStops));
      setHasOutboundOrderBeenValidated(true);
      setMode('create');
    } catch (err) {
      logger.error('Erreur chargement circuit pour édition:', err);
      alert(`Erreur lors du chargement du circuit: ${err.message}`);
    }
  };

  const handleDeleteRoute = (route) => {
    if (!route?.id) {
      toast.error('Erreur: Circuit introuvable');
      return;
    }
    setRouteToDelete(route);
    setDeleteModalOpen(true);
  };

  const confirmDeleteRoute = async () => {
    if (!routeToDelete) return;
    const routeName = routeToDelete?.route_name || routeToDelete?.name || 'ce circuit';
    setDeleteLoading(true);
    try {
      const response = await unifiedApi.routes.delete(routeToDelete.id);
      if (response.success) {
        toast.success(`Circuit "${routeName}" supprimé avec succès !`);
        setDeleteModalOpen(false);
        setRouteToDelete(null);
        await loadData();
      } else {
        throw new Error(response.message || 'Erreur lors de la suppression du circuit');
      }
    } catch (err) {
      let msg = err.message;
      if (err.responseData?.data?.error) msg = err.responseData.data.error;
      else if (err.responseData?.data?.message) msg = err.responseData.data.message;
      toast.error(msg);
      setDeleteModalOpen(false);
      setRouteToDelete(null);
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    loading,
    error,
    mode,
    setMode,
    stops,
    buses,
    drivers,
    routes,
    schools,
    selectedStops,
    optimizedOrder,
    inboundOrder,
    selectedBus,
    setSelectedBus,
    selectedDriver,
    setSelectedDriver,
    busParksAtHome,
    setBusParksAtHome,
    circuitName,
    setCircuitName,
    circuitDescription,
    setCircuitDescription,
    circuitCode,
    setCircuitCode,
    circuitStatus,
    setCircuitStatus,
    editingRouteId,
    isCreating,
    orderingModalOpen,
    orderingDirection,
    setOrderingDirection,
    orderingStep,
    draftOutboundOrder,
    setDraftOutboundOrder,
    draftInboundOrder,
    setDraftInboundOrder,
    dragInsertIndex,
    setDragInsertIndex,
    coherenceResult,
    viewModalRoute,
    viewModalStopsRaw,
    viewModalStopsSorted,
    viewModalLoading,
    viewModalError,
    viewModalSearch,
    setViewModalSearch,
    viewDirection,
    setViewDirection,
    deleteModalOpen,
    routeToDelete,
    deleteLoading,
    dynamicStats,
    loadData,
    resetForm,
    handleStopSelection,
    handleCreateCircuit,
    openOrderingModal,
    closeOrderingModal,
    goToOrderingStep2,
    goBackToStep1,
    resetEveningToInverse,
    handleDraftReorder,
    validateOrderingModal,
    handleViewRoute,
    closeViewModal,
    handleEditRoute,
    handleDeleteRoute,
    confirmDeleteRoute,
    onDeleteClose: () => {
      setDeleteModalOpen(false);
      setRouteToDelete(null);
    },
  };
};
